import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, DialogActions } from "@mui/material";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import {
  SUPER_ADMIN,
  allowedStatusForApprove,
  applicationApprovalStatusTypes,
  dcsAdmin3Roles,
  dcsAdmin4Roles,
  otherUtilitiesAdmin3Roles,
  otherUtilitiesApplicationAdmin3Roles,
  otherUtilitiesApplicationAdmin4Roles,
  powerAdmin3Roles,
  powerAdmin4Roles,
} from "common/constant";
import CommentInputForReason from "./CommentInputForReason";
import ConfirmationDialog from "./ConfirmationDialog";

import { getAllowedStatus, isAuthorizedUser } from "utils";
import { getApprovalStatusForOtherUtilities } from "utils/getApprovalStatusFOrOtherUtilities";

import { usePutApplicationDetailStatusMutation } from "apis/ApplicationRequestDetail";

const allowedRoleForApplicationRevoke = [
  ...powerAdmin3Roles,
  ...dcsAdmin3Roles,
  ...otherUtilitiesAdmin3Roles,
];

const ApplicationApprovalActionButton = ({
  applicationData,
  handleClose,
  isOtherUtilityApplication = false,
  t,
}) => {
  const [isRejectionCommentModalVisible, setIsRejectionCommentModalVisible] =
    useState(false);
  const [isRevokedCommentModalVisible, setIsRevokedCommentModalVisible] =
    useState(false);
  const [isApproveButtonClicked, setIsApproveButtonClicked] = useState(false);

  const {
    approvedBy,
    revokedBy,
    rejectedBy,
    id: applicationId,
    utilities = [],
    applicationStatus: { name: applicationStatusName } = {},
  } = applicationData;

  const { roles } = useSelector((state) => state.Login);
  const [updateStatus, { isLoading: statusLoading }] =
    usePutApplicationDetailStatusMutation();

  const canRevokeApplication = useMemo(
    () => roles && isAuthorizedUser(roles, allowedRoleForApplicationRevoke),
    [roles]
  );

  const { canApproved, canRejected, canRevoked } = isOtherUtilityApplication
    ? getApprovalStatusForOtherUtilities(
        {
          approvedBy,
          rejectedBy,
          revokedBy,
        },
        roles,
        utilities
      )
    : getAllowedStatus(
        {
          approvedBy,
          rejectedBy,
          revokedBy,
        },
        roles
      );

  const submitHandlerForApproval = async () => {
    try {
      await updateStatus({
        applicationId: applicationId,
        status: applicationApprovalStatusTypes["Approved"],
      });
      handleClose();
    } catch (err) {}
  };

  const submitHandlerForRejection = async (comment) => {
    try {
      await updateStatus({
        applicationId: applicationId,
        status: applicationApprovalStatusTypes["Rejected"],
        comment,
      });
      handleClose();
    } catch (err) {}
  };

  const submitHandlerForRevoked = async (comment) => {
    try {
      await updateStatus({
        applicationId: applicationId,
        status: applicationApprovalStatusTypes["Revoked"],
        comment,
      });
      handleClose();
      setIsApproveButtonClicked(false);
    } catch (err) {}
  };

  return (
    <>
      {isApproveButtonClicked && (
        <ConfirmationDialog
          message="Are you sure you want to approve application?"
          onCancel={() => setIsApproveButtonClicked(false)}
          onConfirm={submitHandlerForApproval}
          isOpen={isApproveButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={statusLoading}
        />
      )}
      <CommentInputForReason
        handleClose={() => {
          setIsRejectionCommentModalVisible(false);
        }}
        handleSubmit={submitHandlerForRejection}
        isOpen={isRejectionCommentModalVisible}
        isLoading={statusLoading && isRejectionCommentModalVisible}
        title="Add Reason for Rejection"
        errorMessage="Please enter reason for rejection"
      />
      <CommentInputForReason
        handleClose={() => {
          setIsRevokedCommentModalVisible(false);
        }}
        handleSubmit={submitHandlerForRevoked}
        isOpen={isRevokedCommentModalVisible}
        isLoading={statusLoading && isRevokedCommentModalVisible}
        title="Add Reason for Revoked"
        errorMessage="Please enter reason for revoked"
      />
      {isAuthorizedUser(roles, [
        ...powerAdmin4Roles,
        ...powerAdmin3Roles,
        ...dcsAdmin3Roles,
        ...dcsAdmin4Roles,
        ...otherUtilitiesApplicationAdmin3Roles,
        ...otherUtilitiesApplicationAdmin4Roles,
        SUPER_ADMIN,
      ]) &&
        allowedStatusForApprove.includes(applicationStatusName) && (
          <DialogActions className="border-top">
            <Button
              variant="contained"
              color="success"
              onClick={() => setIsApproveButtonClicked(true)}
              disabled={!canApproved || statusLoading}
            >
              {t("Approve")}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setIsRejectionCommentModalVisible(true)}
              disabled={!canRejected}
            >
              {t("Reject")}
            </Button>
            {canRevokeApplication && (
              <Button
                variant="contained"
                onClick={() => setIsRevokedCommentModalVisible(true)}
                disabled={!canRevoked}
              >
                {t("Revoke")}
              </Button>
            )}
          </DialogActions>
        )}
    </>
  );
};

ApplicationApprovalActionButton.propTypes = {
  applicationData: PropTypes.object,
  handleClose: PropTypes.func,
  isOtherUtilityApplication: PropTypes.bool,
  t: PropTypes.func,
};
export default withTranslation()(ApplicationApprovalActionButton);
